import { useCallback, useMemo, useState } from 'react';
import { toggleCustomerBlockState } from '../../infra/customerRepository'
import type { CustomerAccountRow } from '../../domain/customerTypes';
import type { OperationalConfirmModalProps } from '../../../shared/modals/OperationalConfirmModal';
import { showError, showSuccess } from '../../../shared/ui/appToast';

type Params = {
  canEdit: boolean;
  refreshCustomerTotalCount: () => Promise<void>;
  loadCustomers: () => Promise<void>;
  detailCustomer: CustomerAccountRow | null;
  setDetailCustomer: (customer: CustomerAccountRow | null) => void;
};

export function useCustomerBlockFlow({
  canEdit,
  refreshCustomerTotalCount,
  loadCustomers,
  detailCustomer,
  setDetailCustomer,
}: Params) {
  const [pendingCustomer, setPendingCustomer] = useState<CustomerAccountRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const requestToggleBlock = useCallback(
    (customer: CustomerAccountRow) => {
      if (!canEdit) {
        showError('Modo solo lectura', 'Tu perfil no puede bloquear ni desbloquear clientes.');
        return;
      }
      setPendingCustomer(customer);
    },
    [canEdit]
  );

  const closeBlockConfirm = useCallback(() => {
    if (submitting) return;
    setPendingCustomer(null);
  }, [submitting]);

  const confirmToggleBlock = useCallback(async () => {
    if (!pendingCustomer) return;
    const willBlock = !pendingCustomer.blocked;
    setSubmitting(true);
    try {
      await toggleCustomerBlockState(pendingCustomer.id);
      await refreshCustomerTotalCount();
      await loadCustomers();
      if (detailCustomer?.id === pendingCustomer.id) {
        setDetailCustomer({ ...detailCustomer, blocked: willBlock });
      }
      setPendingCustomer(null);
      showSuccess(
        willBlock ? 'Cliente bloqueado' : 'Cliente desbloqueado',
        willBlock
          ? `«${pendingCustomer.name}» no va a poder comprar a cuenta.`
          : `«${pendingCustomer.name}» puede volver a comprar a cuenta.`
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Ocurrió un error';
      showError(willBlock ? 'No pudimos bloquear' : 'No pudimos desbloquear', message);
    } finally {
      setSubmitting(false);
    }
  }, [
    pendingCustomer,
    refreshCustomerTotalCount,
    loadCustomers,
    detailCustomer,
    setDetailCustomer,
  ]);

  const blockConfirmModalProps = useMemo(() => {
    if (!pendingCustomer) return null;
    const unblocking = pendingCustomer.blocked;
    const copy = unblocking
      ? {
          title: 'Desbloquear cliente',
          message: 'Va a poder volver a comprar a cuenta.',
          confirmLabel: 'Desbloquear',
          destructive: false,
          iconName: 'lock-open-outline' as const,
        }
      : {
          title: 'Bloquear cliente',
          message: 'No va a poder comprar a cuenta. Podés seguir cobrando.',
          confirmLabel: 'Bloquear',
          destructive: true,
          iconName: 'ban-outline' as const,
        };
    const confirmLabel = submitting
      ? unblocking
        ? 'Desbloqueando…'
        : 'Bloqueando…'
      : copy.confirmLabel;
    return {
      ...copy,
      confirmLabel,
      entityName: pendingCustomer.name,
      loading: submitting,
    } satisfies Omit<OperationalConfirmModalProps, 'visible' | 'presentation' | 'onClose' | 'onConfirm'>;
  }, [pendingCustomer, submitting]);

  return {
    requestToggleBlock,
    closeBlockConfirm,
    confirmToggleBlock,
    blockConfirmModalProps,
  };
}
